import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import StationCheckIn from '../components/StationCheckIn';
import SummerPrepPanel from '../components/SummerPrepPanel';
import DailyWorksheet from '../components/DailyWorksheet';
// CoachGreeting (HeyGen player) temporarily removed from the dashboard while the digital twin is in development.
import LiveClassCard from '../components/LiveClassCard';
import MyMaterials from '../components/MyMaterials';
import DigitalScholarCard from '../components/DigitalScholarCard';
import DailyQuests from '../components/DailyQuests';
import MasteryRadar from '../components/MasteryRadar';
import TrophyCase from '../components/TrophyCase';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Loader2, CheckCircle, Clock, AlertTriangle, BookOpen, 
  GraduationCap, Calendar, Trophy, Target, ChevronRight,
  Camera, Mic, Bell, BarChart3, FileText, User, Users,
  Zap, Star, TrendingUp, AlertCircle, Play, Lock,
  Shield, Video, Upload, X, Sparkles, Dumbbell
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const SUBJECT_ICONS = {
  math: '∑', ela: 'Aa', science: '⚗', social_studies: '🌐', pe: '💪', electives: '★'
};

const SchoolDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [briefingAudio, setBriefingAudio] = useState(null);
  const [playingBriefing, setPlayingBriefing] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showCourse, setShowCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [showDrillUpload, setShowDrillUpload] = useState(false);
  const [drillUploading, setDrillUploading] = useState(false);
  const [drillResult, setDrillResult] = useState(null);
  const [drillProgress, setDrillProgress] = useState(0);
  const [provisioning, setProvisioning] = useState(false);
  const [autoProvisionTried, setAutoProvisionTried] = useState(false);

  const token = localStorage.getItem('academy_token');

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, electivesRes] = await Promise.all([
        api.get('/school/dashboard', { headers: { Authorization: `Bearer ${token}` } }),
        api.get('/school/electives/active', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { electives: [] } })),
      ]);
      setDashboard({ ...dashRes.data, active_electives: electivesRes.data?.electives || [] });
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/academy/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Presence heartbeat so the teacher dashboard can show who is online now
  useEffect(() => {
    const tok = localStorage.getItem('academy_token');
    if (!tok) return;
    const ping = () => api.post('/academy/presence/ping', {}, { headers: { Authorization: `Bearer ${tok}` } }).catch(() => {});
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, []);

  const handleCheckIn = async () => {
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      setCameraStream(stream);
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      toast.error('Camera access denied. Using manual check-in.');
      setShowCamera(false);
      manualCheckIn();
    }
  };

  const captureAndVerify = async () => {
    setCheckingIn(true);
    try {
      const video = videoRef.current;
      // Run MediaPipe FaceLandmarker locally on the live video frame — zero API cost
      const { verifyFaceFromElement } = await import('../lib/mediapipe');
      const verification = await verifyFaceFromElement(video);

      // Stop camera (no image leaves the device)
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
      setShowCamera(false);

      if (!verification.verified) {
        toast.error(verification.reason || 'Face not verified. Center your face in the frame and try again.');
        return;
      }

      // Send only the structured verification result (no image bytes)
      const res = await api.post('/school/attendance/biometric-check-in',
        { verification },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        toast.success(`${res.data.message || 'Identity verified.'} Confidence ${verification.confidence}%`);
        if (res.data.briefing) {
          setBriefingAudio(res.data.briefing);
          playBriefing(res.data.briefing);
        }
        loadDashboard();
      } else {
        toast.error(res.data.message || 'Verification failed. Try manual check-in.');
      }
    } catch (err) {
      console.error('MediaPipe check-in error:', err);
      toast.error('Local verification failed. Falling back to manual check-in.');
      manualCheckIn();
    } finally {
      setCheckingIn(false);
    }
  };

  const cancelCamera = () => {
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop());
    setCameraStream(null);
    setShowCamera(false);
  };

  const manualCheckIn = async () => {
    setCheckingIn(true);
    try {
      const res = await api.post('/school/attendance/check-in', {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success('Checked in manually.');
        if (res.data.briefing) {
          setBriefingAudio(res.data.briefing);
          playBriefing(res.data.briefing);
        }
        loadDashboard();
      }
    } catch (err) {
      toast.error('Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const playBriefing = (briefing) => {
    if (!briefing?.text) return;
    setPlayingBriefing(true);
    if (briefing.audio_base64) {
      const audio = new Audio(`data:audio/mpeg;base64,${briefing.audio_base64}`);
      audio.onended = () => setPlayingBriefing(false);
      audio.play().catch(() => browserTTS(briefing.text));
    } else {
      browserTTS(briefing.text);
    }
  };

  const browserTTS = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 0.9;
      utterance.onend = () => setPlayingBriefing(false);
      window.speechSynthesis.speak(utterance);
    } else {
      setPlayingBriefing(false);
    }
  };

  const handleDrillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDrillUploading(true);
    setDrillProgress(0);
    try {
      // Run MediaPipe PoseLandmarker locally on the video — zero API cost
      const { scoreDrillVideo } = await import('../lib/mediapipe');
      const poseResult = await scoreDrillVideo(file, (p) => setDrillProgress(p));

      // Send only the structured score (no video bytes leave the device)
      const res = await api.post('/school/performance/upload',
        { pose_result: poseResult, drill_type: 'general', notes: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDrillResult(res.data);
      if (res.data.success) {
        toast.success(`Drill scored locally: ${res.data.performance?.score}%`);
        if (res.data.feedback?.text) playBriefing(res.data.feedback);
      } else {
        toast.error(res.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('MediaPipe drill scoring error:', err);
      toast.error('Local drill scoring failed. Try a shorter, well-lit clip.');
    } finally {
      setDrillUploading(false);
      setDrillProgress(0);
    }
  };

  const loadCourseModules = async (courseId) => {
    setModulesLoading(true);
    setShowCourse(courseId);
    try {
      const res = await api.get(`/school/course/${courseId}/modules`, { headers: { Authorization: `Bearer ${token}` } });
      setModules(res.data.modules || []);
    } catch (err) {
      toast.error('Failed to load modules');
    } finally {
      setModulesLoading(false);
    }
  };

  const startQuiz = (module) => {
    setActiveQuiz(module);
    setQuizAnswers({});
    setQuizResult(null);
  };

  const submitQuiz = async () => {
    if (!activeQuiz) return;
    setSubmittingQuiz(true);
    try {
      const res = await api.post('/school/quiz/submit',
        { module_id: activeQuiz.id, answers: quizAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizResult(res.data);
      toast.success(`Score: ${res.data.score}% (${res.data.letter_grade})`);
      loadDashboard();
    } catch (err) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  // Brandon-only escape hatch: triggers seed_demo_brandon to provision
  // the 6 CA-standards-aligned courses + 60 days of attendance + grade history.
  // Supports silent mode for auto-trigger (no toasts on failure since the user
  // didn't actively request it).
  const provisionCurriculum = async ({ silent = false } = {}) => {
    setProvisioning(true);
    try {
      const res = await api.post(
        '/school/admin/seed-demo',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        if (!silent) toast.success('Curriculum provisioned. Reloading…');
        await loadDashboard();
      } else if (!silent) {
        toast.error(res.data?.message || 'Provisioning failed.');
      }
    } catch (err) {
      if (!silent) {
        const msg = err.response?.data?.detail || 'Provisioning failed. Admin access required.';
        toast.error(msg);
      }
    } finally {
      setProvisioning(false);
    }
  };

  // Auto-trigger provisioning once per session whenever an admin/founder lands on
  // an empty dashboard. Falls back to the manual button if the endpoint denies
  // (non-admin user) or errors.
  useEffect(() => {
    if (!dashboard || autoProvisionTried || provisioning) return;
    const courseCount = dashboard?.academics?.courses?.length || 0;
    const stu = dashboard?.student || {};
    const isAdminLike = !!(stu.is_admin || stu.is_founder || stu.admin_role);
    if (courseCount === 0) {
      setAutoProvisionTried(true);
      if (isAdminLike) provisionCurriculum({ silent: true });
    }
  }, [dashboard, autoProvisionTried, provisioning]);

  const requestTranscript = async () => {
    try {
      const res = await api.get('/school/transcript', { headers: { Authorization: `Bearer ${token}` } });
      const t = res.data?.transcript;
      if (!t) {
        toast.error('Transcript not available yet');
        return;
      }
      // Open transcript in a printable preview window
      const html = renderTranscriptHtml(t);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(html);
        w.document.close();
      } else {
        toast.error('Pop-up blocked — allow pop-ups to view your transcript');
      }
      toast.success(`Transcript ready · GPA ${t.cumulative_gpa.toFixed(2)}`);
    } catch (err) {
      toast.error('Failed to load transcript');
    }
  };

  const renderTranscriptHtml = (t) => {
    const rows = (t.academic_record || []).map(c => `
      <tr>
        <td>${c.subject}</td>
        <td>${c.term}</td>
        <td>${c.teacher || ''}</td>
        <td style="text-align:center">${c.final_grade}</td>
        <td style="text-align:right">${c.percentage}%</td>
        <td style="text-align:center">${c.gpa_points}</td>
        <td style="text-align:center">${c.credits}</td>
      </tr>
    `).join('');
    return `<!doctype html><html><head><title>Academic Transcript — ${t.student.name}</title>
      <style>
        body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:30px;color:#111;background:#faf9f6}
        h1{font-size:24px;margin:0;letter-spacing:.5px}.sub{color:#666;font-size:12px;text-transform:uppercase;letter-spacing:2px}
        .header{border-bottom:3px double #111;padding-bottom:14px;margin-bottom:18px}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:13px;margin:14px 0}
        table{width:100%;border-collapse:collapse;margin-top:18px;font-size:13px}
        th,td{border:1px solid #ccc;padding:8px 10px}
        th{background:#111;color:#fff;text-transform:uppercase;font-size:11px;letter-spacing:1px}
        .summary{margin-top:20px;padding:14px;background:#111;color:#fff}
        .summary div{margin:4px 0;display:flex;justify-content:space-between}
        .seal{margin-top:24px;padding:10px;border:1px dashed #999;font-size:11px;color:#888;text-align:center}
        @media print{body{background:#fff}}
      </style></head><body>
      <div class="header">
        <h1>OFFICIAL ACADEMIC TRANSCRIPT</h1>
        <div class="sub">PRSVR · The Sovereign Academy</div>
      </div>
      <div class="grid">
        <div><b>Student:</b> ${t.student.name}</div>
        <div><b>Student ID:</b> ${t.student.student_id}</div>
        <div><b>Grade Level:</b> ${t.student.grade_level}</div>
        <div><b>Email:</b> ${t.student.email}</div>
        <div><b>School:</b> ${t.student.school}</div>
        <div><b>Accreditation:</b> ${t.student.accreditation}</div>
      </div>
      <table>
        <thead><tr><th>Course</th><th>Term</th><th>Teacher</th><th>Grade</th><th>Score</th><th>GPA</th><th>Credits</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="summary">
        <div><span>Cumulative GPA</span><span>${t.cumulative_gpa.toFixed(2)} / 4.00</span></div>
        <div><span>Total Credits Earned</span><span>${t.total_credits}</span></div>
        <div><span>Attendance Rate</span><span>${t.attendance.rate}% (${t.attendance.present}/${t.attendance.total_days})</span></div>
        <div><span>Generated</span><span>${new Date(t.generated_at).toLocaleString()}</span></div>
      </div>
      <div class="seal">${t.disclaimer}</div>
      <div style="text-align:center;margin-top:18px"><button onclick="window.print()" style="padding:10px 20px;background:#111;color:#fff;border:0;cursor:pointer">PRINT TRANSCRIPT</button></div>
      </body></html>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 uppercase tracking-[0.3em] text-xs">Loading Command Center</p>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const { student, attendance, academics, counselor, bell_schedule, schedule, announcement, ncaa, athletics } = dashboard;
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-16" data-testid="school-dashboard">
      {/* Top Command Bar */}
      <div className="bg-gradient-to-r from-[#0a0a0a] via-[#111] to-[#0a0a0a] border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3">
                <img src="/logos/saa-athlete-mark.png" alt="Scholar Athlete Academy" className="h-12 w-auto object-contain" />
                <div>
                  <h1 className="text-white font-black text-lg tracking-tight">{student.name}</h1>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-orange-500 font-bold uppercase tracking-wider">Grade {student.grade_level}</span>
                    <span className="text-gray-600">|</span>
                    <span className="text-gray-500">{student.sport || 'Scholar-Athlete'}</span>
                    <span className="text-gray-600">|</span>
                    <span className="text-gray-500 font-mono">ID: {student.student_id}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              {(student.is_admin || student.admin_role) && (
                <button
                  onClick={() => navigate('/academy/teacher')}
                  className="hidden sm:flex items-center gap-2 border-2 border-orange-500/50 hover:border-orange-500 bg-orange-500/10 hover:bg-orange-500/20 px-4 py-2.5 transition-all"
                  data-testid="open-teacher-portal-btn"
                  title="Daily lesson plans + AI-generated application exercises"
                >
                  <Sparkles className="w-3.5 h-3.5 text-orange-500" />
                  <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-orange-400">
                    Teacher Portal
                  </span>
                </button>
              )}
              <div className="text-right">
                <div className="text-2xl font-black text-white font-mono">{timeStr}</div>
                <div className="text-xs text-gray-500">{dateStr}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Always-on Live Class card (Jitsi) — Join appears the moment a coach goes live */}
        <LiveClassCard isTeacher={false} />

        {/* The Combine — sports-centric endless practice (GameDay Skill Engine) */}
        <a href="/academy/combine" data-testid="combine-cta"
           className="group mb-6 flex items-center gap-4 border-2 border-orange-500/50 bg-gradient-to-r from-orange-500/15 to-transparent rounded-xl p-4 hover:border-orange-500 transition-colors">
          <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center flex-shrink-0">
            <Dumbbell className="w-6 h-6 text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.3em] text-orange-500/80 font-black">GameDay Skill Engine</div>
            <div className="text-lg font-black text-white leading-tight">Hit The Combine</div>
            <div className="text-[11px] text-zinc-400">Sports-centric reps to stay sharp — get on a streak and catch fire. Counts toward your Scholar Rating.</div>
          </div>
          <ChevronRight className="w-5 h-5 text-orange-500/60 group-hover:text-orange-500 flex-shrink-0" />
        </a>

        {/* Materials sent by the teacher */}
        <MyMaterials />

        {/* Quick resource: supply list */}
        <div className="mb-4">
          <a href="/downloads/prsvr-supply-list.png" download="PRSVR-Supply-List.png" className="inline-flex items-center gap-2 border-2 border-zinc-800 hover:border-orange-500 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-orange-400 transition-colors" data-testid="supply-list-download">
            <FileText className="w-4 h-4" /> Download Supply List
          </a>
        </div>

        {/* Digital Scholar Card — My Player header */}
        <DigitalScholarCard />

        {/* Daily Quests + XP */}
        <DailyQuests />

        {/* Mastery Radar */}
        <MasteryRadar />

        {/* Certified Scholar Badges */}
        <TrophyCase />

        {/* 6-Week Elective Courses entry point */}
        <div
          onClick={() => navigate('/academy/elective-courses')}
          className="mb-6 border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/10 to-transparent p-5 cursor-pointer hover:border-orange-500 transition-colors group"
          data-testid="electives-entry-card"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-black" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.25em] text-orange-400">Elective Academy</div>
                <h3 className="text-lg font-black text-white leading-tight">6-Week Applied Electives</h3>
                <p className="text-xs text-gray-500 mt-0.5">Math of Basketball · NIL &amp; Personal Branding · Social Media &amp; Media Training — earn certificates &amp; badge stamps.</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-orange-500 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Summer Prep Bridge + countdown to Aug 24 first day */}
        <SummerPrepPanel />

        {/* Today's daily worksheet — built from the day's lesson */}
        <DailyWorksheet />

        {/* Live station check-in + exit ticket (feeds Teacher Game Control) */}
        <StationCheckIn />

        {/* EMPTY STATE — No courses provisioned yet */}
        {academics.courses.length === 0 && (
          <div
            className="mb-6 border-2 border-orange-500/60 bg-gradient-to-br from-orange-500/15 to-transparent p-6"
            data-testid="empty-curriculum-banner"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                {provisioning ? (
                  <Loader2 className="w-6 h-6 text-black animate-spin" />
                ) : (
                  <BookOpen className="w-6 h-6 text-black" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold text-orange-500 uppercase tracking-[0.3em] mb-1">
                  {provisioning ? 'Provisioning Curriculum' : 'Curriculum Not Provisioned'}
                </div>
                <div className="text-white font-bold text-lg leading-tight">
                  {provisioning
                    ? 'Loading your California-aligned courses and recent activity…'
                    : "Your California-aligned courses haven't been loaded into this environment yet."}
                </div>
                <div className="text-xs text-gray-400 mt-2 leading-relaxed">
                  {provisioning
                    ? 'Seeding 6 Grade 8 courses, 60 days of attendance history, graded modules, and your live quiz queue. Usually takes ~5 seconds.'
                    : 'Tap below to provision 6 Grade 8 courses (Math, ELA, Science, Social Studies, PE, Electives), 60 days of attendance history, graded modules, and a live quiz queue. Admin/founder accounts only.'}
                </div>
              </div>
              {!provisioning && (
                <Button
                  onClick={() => provisionCurriculum()}
                  disabled={provisioning}
                  className="bg-orange-500 hover:bg-orange-400 text-black font-black text-xs uppercase tracking-[0.2em] px-6 py-6 disabled:bg-gray-800 disabled:text-gray-500"
                  data-testid="provision-curriculum-btn"
                >
                  Provision Curriculum <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* JUMP TO NEXT LESSON — sticky shortcut when courses exist but no "due today" alert is active */}
        {academics.courses.length > 0 && (!academics.due_today || academics.due_today.length === 0) && (
          <div
            className="mb-6 border-2 border-white/20 bg-white/[0.03] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="jump-to-lesson-banner"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-white/60 uppercase tracking-[0.3em]">Next Lesson</div>
              <div className="text-white font-bold text-sm mt-1">
                {academics.courses[0].name} —{' '}
                <span className="text-orange-400">
                  {academics.courses[0].next_module_title || 'Continue where you left off'}
                </span>
              </div>
            </div>
            <Button
              onClick={() => loadCourseModules(academics.courses[0].id)}
              className="bg-white hover:bg-orange-400 text-black font-black text-xs uppercase tracking-[0.2em]"
              data-testid="jump-to-lesson-btn"
            >
              Open Lesson <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* L'CHAIM SYNERGY — discovery card for scholars who haven't enrolled, active badge for those who have */}
        {academics.courses.length > 0 && (
          student.lchaim_tier ? (
            <div
              className="mb-6 border-2 p-3 flex items-center justify-between gap-3"
              style={{ borderColor: '#00d4ff60', background: 'linear-gradient(90deg, rgba(0,212,255,0.08), transparent)' }}
              data-testid="lchaim-active-badge"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 flex items-center justify-center flex-shrink-0"
                     style={{ background: '#00d4ff', color: '#0a1628' }}>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: '#00d4ff' }}>
                    L&apos;Chaim Active
                  </div>
                  <div className="text-white text-sm font-bold truncate">
                    Your daily discipline journey is running
                  </div>
                </div>
              </div>
              <Button
                onClick={() => navigate('/academy/lchaim/dashboard')}
                className="font-black text-xs uppercase tracking-[0.2em] flex-shrink-0"
                style={{ background: '#00d4ff', color: '#0a1628' }}
                data-testid="lchaim-open-dashboard-btn"
              >
                Open L&apos;Chaim <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ) : (
            <div
              className="mb-6 border-2 p-5"
              style={{ borderColor: '#00d4ff80', background: 'linear-gradient(135deg, rgba(10,22,40,0.6), rgba(0,212,255,0.05))' }}
              data-testid="lchaim-synergy-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                     style={{ background: '#00d4ff', color: '#0a1628' }}>
                  <Sparkles className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] mb-1" style={{ color: '#00d4ff' }}>
                    Add L&apos;Chaim to your journey
                  </div>
                  <div className="text-white font-black text-base sm:text-lg leading-tight">
                    Pair your scholar-athlete curriculum with a 350-day discipline & faith journey.
                  </div>
                  <div className="text-xs text-gray-400 mt-1.5 leading-relaxed">
                    Same login. Single dashboard. Daily scriptural pillars, activity prompts, and reflection vault — designed to compound character alongside your academics.
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/academy/lchaim')}
                  className="font-black text-xs uppercase tracking-[0.2em] flex-shrink-0 px-5 py-5"
                  style={{ background: '#dc2626', color: '#fff' }}
                  data-testid="lchaim-add-btn"
                >
                  Explore Tiers <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )
        )}

        {/* DUE TODAY Alert */}
        {academics.due_today && academics.due_today.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-orange-500/20 via-orange-500/10 to-transparent border-2 border-orange-500/40 rounded-xl p-4 animate-pulse-slow" data-testid="due-today-banner">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold text-orange-500 uppercase tracking-[0.25em]">Due Today</div>
                <div className="text-white font-bold text-base mt-0.5">
                  {academics.due_today[0].title}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {academics.due_today[0].course} · {academics.due_today[0].points} pts · Standard {academics.due_today[0].standard_code}
                </div>
              </div>
              <Button
                onClick={() => loadCourseModules(academics.due_today[0].course_id)}
                className="bg-orange-500 hover:bg-orange-400 text-black font-black text-xs uppercase tracking-wider"
                data-testid="due-today-take-quiz"
              >
                Take Quiz Now <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Counselor Greeting */}
        <div className="mb-6 bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-xl p-4" data-testid="counselor-greeting">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Mic className="w-4 h-4 text-black" />
            </div>
            <div className="flex-1">
              <div className="text-xs text-orange-500 font-bold uppercase tracking-wider mb-1">School Counselor</div>
              <p className="text-gray-300 text-sm leading-relaxed">{counselor.greeting}</p>
              {counselor.voice_status === 'coming_soon' && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  <Play className="w-3 h-3" /> Voice check-in coming soon
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {/* Attendance */}
          <Card className="bg-[#111] border-gray-800/50 hover:border-orange-500/30 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">Attendance</span>
                {attendance.checked_in_today ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                )}
              </div>
              <div className="text-2xl font-black text-white">{attendance.rate}%</div>
              {!attendance.checked_in_today && (
                <Button
                  onClick={handleCheckIn}
                  disabled={checkingIn}
                  size="sm"
                  className="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs h-7"
                  data-testid="check-in-btn"
                >
                  {checkingIn ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Camera className="w-3 h-3 mr-1" /> CHECK IN</>}
                </Button>
              )}
              {attendance.checked_in_today && (
                <div className="text-xs text-green-500/80 mt-1">Present today</div>
              )}
            </CardContent>
          </Card>

          {/* GPA */}
          <Card className="bg-[#111] border-gray-800/50 hover:border-orange-500/30 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">GPA</span>
                <Trophy className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-2xl font-black text-white">{academics.cumulative_gpa.toFixed(2)}</div>
              <div className="text-xs text-gray-500 mt-1">of 4.00 scale</div>
            </CardContent>
          </Card>

          {/* Courses */}
          <Card className="bg-[#111] border-gray-800/50 hover:border-orange-500/30 transition-all">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">Courses</span>
                <BookOpen className="w-4 h-4 text-blue-500" />
              </div>
              <div className="text-2xl font-black text-white">{academics.courses.length}</div>
              <div className="text-xs text-gray-500 mt-1">Active this term</div>
            </CardContent>
          </Card>

          {/* Missing Work */}
          <Card className={`bg-[#111] border-gray-800/50 ${academics.total_missing > 0 ? 'border-red-500/30' : 'hover:border-orange-500/30'} transition-all`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">Missing</span>
                {academics.total_missing > 0 ? (
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
              <div className={`text-2xl font-black ${academics.total_missing > 0 ? 'text-red-500' : 'text-white'}`}>
                {academics.total_missing}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {academics.total_missing > 0 ? 'Need attention' : 'All caught up'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* NCAA Eligibility + Athletic Scout Bar */}
        {ncaa && (
          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <Card className={`bg-[#111] border ${ncaa.risk_level === 'elite' ? 'border-green-500/30' : ncaa.risk_level === 'lockout' ? 'border-red-500/30' : 'border-amber-500/30'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest">CIF / High-School Eligibility</span>
                  <Shield className={`w-4 h-4 ${ncaa.d1_eligible ? 'text-green-500' : 'text-amber-500'}`} />
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-lg font-black text-white">Core GPA: {ncaa.core_gpa.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">D1 requires {ncaa.d1_gpa_required} | D2 requires {ncaa.d2_gpa_required}</div>
                  </div>
                  <div className="ml-auto text-right">
                    <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${
                      ncaa.risk_level === 'elite' ? 'bg-green-500/20 text-green-400' :
                      ncaa.risk_level === 'on_track' ? 'bg-blue-500/20 text-blue-400' :
                      ncaa.risk_level === 'at_risk' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {ncaa.risk_level === 'elite' ? 'ELITE' : ncaa.risk_level === 'on_track' ? 'ON TRACK' : ncaa.risk_level === 'at_risk' ? 'AT RISK' : 'ACADEMIC HOLD'}
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <Progress value={Math.min((ncaa.core_gpa / 4.0) * 100, 100)} className="h-1.5" />
                </div>
              </CardContent>
            </Card>

            <Card className={`bg-[#111] border ${athletics?.unlocked ? 'border-orange-500/30' : 'border-red-500/30'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-gray-500 uppercase tracking-widest">Athletic Scout</span>
                  {athletics?.unlocked ? <Target className="w-4 h-4 text-orange-500" /> : <Lock className="w-4 h-4 text-red-500" />}
                </div>
                {athletics?.unlocked ? (
                  <>
                    <div className="text-lg font-black text-white">
                      {athletics.recent_drills?.length > 0 ? `${athletics.recent_drills[0]?.score}% Latest` : 'No Drills Yet'}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">Upload a drill video for biomechanics analysis</div>
                    <Button
                      onClick={() => setShowDrillUpload(true)}
                      size="sm"
                      className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs h-7"
                      data-testid="drill-upload-btn"
                    >
                      <Video className="w-3 h-3 mr-1" /> Upload Drill
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-black text-red-400">LOCKED</div>
                    <div className="text-xs text-red-400/70">{athletics?.lockout_reason}</div>
                    <div className="text-xs text-gray-600 mt-1">Raise core GPA to {ncaa.athletic_lockout_gpa} to unlock</div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Courses */}
          <div className="lg:col-span-2 space-y-4">
            {/* ACTIVE ELECTIVES — currently in-progress */}
            {dashboard.active_electives && dashboard.active_electives.length > 0 && (
              <div className="bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/30 rounded-xl p-4" data-testid="active-electives-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400" />
                    <h2 className="text-xs font-bold text-amber-400 uppercase tracking-widest">Active Electives · In Progress</h2>
                  </div>
                  <button
                    onClick={() => navigate('/academy/electives')}
                    className="text-[10px] text-amber-400 uppercase tracking-widest font-bold hover:underline"
                    data-testid="electives-shop-more"
                  >
                    Shop More →
                  </button>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {dashboard.active_electives.map((e) => (
                    <button
                      key={e.id}
                      onClick={() => navigate('/academy/electives')}
                      className="bg-[#0a0a0a] border border-amber-500/20 rounded-lg p-3 text-left hover:border-amber-500/60 transition-all group"
                      data-testid={`active-elective-${e.elective_id}`}
                    >
                      <div className="text-[9px] uppercase tracking-widest text-amber-400/70 mb-1">{e.instructor || 'Faculty'}</div>
                      <div className="text-sm font-bold text-white leading-tight mb-2 line-clamp-2 min-h-[2.5rem]">{e.title}</div>
                      {/* Progress bar */}
                      <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden mb-1.5">
                        <div className="h-full bg-amber-400" style={{ width: `${e.progress || 0}%` }} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-500">{e.progress || 0}% complete</span>
                        {e.next_session && (
                          <span className="text-[10px] text-amber-400/80 font-bold">
                            {new Date(e.next_session).toLocaleDateString(undefined, { weekday: 'short' })}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest">My Courses</h2>
              <span className="text-xs text-gray-600">CA State Standards Aligned</span>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {academics.courses.map((course) => (
                <button
                  key={course.id}
                  onClick={() => loadCourseModules(course.id)}
                  className="bg-[#111] border border-gray-800/50 rounded-xl p-4 text-left hover:border-orange-500/40 transition-all group"
                  data-testid={`course-${course.subject}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-black"
                      style={{ backgroundColor: `${course.color}15`, color: course.color }}
                    >
                      {SUBJECT_ICONS[course.subject] || '📚'}
                    </div>
                    <div className="text-right">
                      <div className={`text-xl font-black ${course.letter === 'N/A' ? 'text-gray-600' : 'text-white'}`}>
                        {course.letter}
                      </div>
                      {course.grade && (
                        <div className="text-xs text-gray-500">{course.grade}%</div>
                      )}
                    </div>
                  </div>
                  <div className="text-white font-bold text-sm group-hover:text-orange-400 transition-colors">
                    {course.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{course.teacher}</div>
                  <ChevronRight className="w-4 h-4 text-gray-700 group-hover:text-orange-500 mt-2 transition-colors" />
                </button>
              ))}
            </div>

            {/* Upcoming Assignments */}
            {academics.upcoming_assignments.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">Upcoming Assignments</h2>
                <div className="space-y-2">
                  {academics.upcoming_assignments.map((a) => (
                    <div key={a.id} className="bg-[#111] border border-gray-800/50 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500/10 rounded flex items-center justify-center">
                        <FileText className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium truncate">{a.title}</div>
                        <div className="text-xs text-gray-500">{a.course}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-orange-500">{a.points} pts</div>
                        <div className="text-xs text-gray-600">
                          {new Date(a.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Assignments */}
            {academics.missing_assignments.length > 0 && (
              <div>
                <h2 className="text-sm font-bold text-red-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Missing Assignments
                </h2>
                <div className="space-y-2">
                  {academics.missing_assignments.map((a) => (
                    <div key={a.id} className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-500/10 rounded flex items-center justify-center">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-medium truncate">{a.title}</div>
                        <div className="text-xs text-red-400">{a.course}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-red-500">{a.points} pts</div>
                        <div className="text-xs text-gray-600">OVERDUE</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Schedule & Announcements */}
          <div className="space-y-4">
            {/* Today's Schedule */}
            <div>
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">Today&apos;s Schedule</h2>
              <div className="bg-[#111] border border-gray-800/50 rounded-xl overflow-hidden">
                {schedule && Object.entries(bell_schedule).map(([key, period]) => {
                  const courseInfo = schedule.periods?.[key];
                  const isBreak = key === 'break' || key === 'lunch' || key === 'athletics';
                  return (
                    <div
                      key={key}
                      className={`px-4 py-3 border-b border-gray-800/30 last:border-0 ${isBreak ? 'bg-gray-900/50' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-[10px] text-gray-600 w-20 font-mono">{period.time.split(' - ')[0]}</div>
                          <div>
                            <div className={`text-sm font-medium ${isBreak ? 'text-gray-500' : 'text-white'}`}>
                              {courseInfo ? courseInfo.course_name : period.label}
                            </div>
                            {courseInfo && (
                              <div className="text-[10px] text-gray-600">{courseInfo.teacher} • Rm {courseInfo.room}</div>
                            )}
                          </div>
                        </div>
                        {courseInfo && (
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: academics.courses.find(c => c.subject === courseInfo.subject)?.color || '#666' }}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Principal Announcement */}
            <div>
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">
                <Bell className="w-3 h-3 inline mr-1" /> Announcement
              </h2>
              <div className="bg-[#111] border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">{announcement.principal}</div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Weekly Address</div>
                  </div>
                </div>
                {announcement.video_status === 'coming_soon' ? (
                  <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center mb-3">
                    <div className="text-center">
                      <Play className="w-6 h-6 text-gray-600 mx-auto mb-1" />
                      <span className="text-xs text-gray-600">Video Coming Soon</span>
                    </div>
                  </div>
                ) : null}
                <p className="text-gray-400 text-sm">{announcement.message}</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => navigate('/academy/electives')}
                className="bg-[#111] border border-gray-800/50 rounded-lg p-3 text-center hover:border-orange-500/40 transition-all"
                data-testid="dashboard-shop-electives"
              >
                <Star className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                <span className="text-xs text-gray-400">Shop Electives</span>
              </button>
              <button
                onClick={requestTranscript}
                className="bg-[#111] border border-gray-800/50 rounded-lg p-3 text-center hover:border-amber-500/40 transition-all"
                data-testid="dashboard-request-transcript"
              >
                <FileText className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                <span className="text-xs text-gray-400">Request Transcript</span>
              </button>
              <button
                onClick={() => navigate('/academy/parent')}
                className="bg-[#111] border border-gray-800/50 rounded-lg p-3 text-center hover:border-purple-500/40 transition-all"
                data-testid="dashboard-parent-portal"
              >
                <Users className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                <span className="text-xs text-gray-400">Parent Portal</span>
              </button>
              <button
                onClick={() => navigate('/academy/high-performance')}
                className="bg-[#111] border border-gray-800/50 rounded-lg p-3 text-center hover:border-blue-500/40 transition-all"
              >
                <BarChart3 className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                <span className="text-xs text-gray-400">Performance</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Course Modules Dialog */}
      <Dialog open={!!showCourse} onOpenChange={() => { setShowCourse(null); setModules([]); }}>
        <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">
              {academics.courses.find(c => c.id === showCourse)?.name || 'Course'} — Modules
            </DialogTitle>
          </DialogHeader>
          {modulesLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div>
          ) : (
            <div className="space-y-3 mt-4">
              {modules.map((mod, i) => (
                <div
                  key={mod.id}
                  className={`border rounded-lg p-4 ${
                    mod.accessible
                      ? mod.completed
                        ? 'border-green-600/30 bg-green-600/5'
                        : 'border-gray-700 bg-[#111] hover:border-orange-500/40'
                      : 'border-gray-800 bg-gray-900/30 opacity-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black ${
                        mod.completed ? 'bg-green-600 text-white' : mod.accessible ? 'bg-orange-500/20 text-orange-500' : 'bg-gray-800 text-gray-600'
                      }`}>
                        {mod.completed ? <CheckCircle className="w-4 h-4" /> : mod.accessible ? i + 1 : <Lock className="w-3 h-3" />}
                      </div>
                      <div>
                        <div className="text-white font-bold text-sm">{mod.title}</div>
                        <div className="text-xs text-gray-500">Standard: {mod.standard_code}</div>
                      </div>
                    </div>
                    {mod.quiz_score !== null && mod.quiz_score !== undefined && (
                      <div className="text-sm font-bold text-green-400">{mod.quiz_score}%</div>
                    )}
                  </div>

                  <p className="text-gray-400 text-xs mb-3">{mod.description}</p>

                  {mod.accessible && (
                    <div className="space-y-3">
                      {/* Deep Dive Lesson */}
                      {mod.content?.deep_dive?.length > 0 && (
                        <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                          <div className="text-xs text-orange-500 font-bold uppercase tracking-wider">The Game Plan</div>
                          {mod.content.deep_dive.map((para, pi) => (
                            <p key={pi} className="text-gray-300 text-xs leading-relaxed">{para}</p>
                          ))}
                        </div>
                      )}

                      {/* Vocabulary Drill */}
                      {mod.content?.vocabulary?.length > 0 && (
                        <div className="bg-gray-900/50 rounded-lg p-4">
                          <div className="text-xs text-orange-500 font-bold uppercase tracking-wider mb-2">Vocabulary Drill</div>
                          <div className="space-y-2">
                            {mod.content.vocabulary.map((v, vi) => (
                              <div key={vi} className="flex gap-2">
                                <span className="text-white font-bold text-xs min-w-[120px]">{v.term}</span>
                                <span className="text-gray-400 text-xs">{v.definition}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sports Logic Case Study */}
                      {mod.content?.sports_logic && (
                        <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                          <div className="text-xs text-orange-500 font-bold uppercase tracking-wider mb-1">Sports Logic: {mod.content.sports_logic.title}</div>
                          <p className="text-gray-300 text-xs leading-relaxed">{mod.content.sports_logic.case_study}</p>
                        </div>
                      )}

                      {/* Video Coming Soon */}
                      {mod.content?.video_status === 'coming_soon' && (
                        <div className="bg-gray-900/50 rounded p-3 flex items-center gap-2">
                          <Play className="w-4 h-4 text-gray-600" />
                          <span className="text-xs text-gray-500">Video lesson coming soon</span>
                        </div>
                      )}

                      {/* Actions Row */}
                      <div className="flex gap-2 flex-wrap">
                        {mod.accessible && (
                          <Button onClick={() => { setShowCourse(null); navigate(`/academy/lesson/${showCourse}/${mod.id}`); }}
                            size="sm" className="bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs flex-1">
                            <BookOpen className="w-3 h-3 mr-1" /> Open Lesson
                          </Button>
                        )}
                        {mod.worksheet && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/school/module/${mod.id}/worksheet-pdf`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-white text-xs font-bold px-3 py-1.5 rounded-md flex-1 justify-center"
                          >
                            <FileText className="w-3 h-3" /> Print Field Work
                          </a>
                        )}
                      </div>
                      {mod.completed && (
                        <div className="flex items-center gap-2 text-green-500 text-xs">
                          <CheckCircle className="w-3 h-3" /> Module Complete — Score: {mod.quiz_score}%
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={!!activeQuiz} onOpenChange={() => { setActiveQuiz(null); setQuizResult(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-black">
              {quizResult ? 'Quiz Results' : activeQuiz?.quiz?.title || 'Mastery Quiz'}
            </DialogTitle>
          </DialogHeader>

          {quizResult ? (
            <div className="py-4">
              <div className="text-center mb-6">
                <div className={`text-5xl font-black mb-2 ${quizResult.passed ? 'text-green-400' : 'text-red-400'}`}>
                  {quizResult.score}%
                </div>
                <div className={`text-lg font-bold ${quizResult.passed ? 'text-green-500' : 'text-red-500'}`}>
                  {quizResult.letter_grade} — {quizResult.passed ? 'PASSED' : 'NEEDS REVIEW'}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {quizResult.earned_points}/{quizResult.total_points} points
                </div>
              </div>
              {quizResult.results.map((r, i) => (
                <div key={i} className={`p-3 rounded mb-2 ${r.correct ? 'bg-green-600/10 border border-green-600/20' : 'bg-red-600/10 border border-red-600/20'}`}>
                  <div className="flex items-center gap-2">
                    {r.correct ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
                    <span className="text-sm text-gray-300">Question {i + 1}: {r.correct ? 'Correct' : 'Incorrect'}</span>
                  </div>
                </div>
              ))}
              <Button onClick={() => { setActiveQuiz(null); setQuizResult(null); }} className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-black font-bold">
                Close
              </Button>
            </div>
          ) : activeQuiz?.quiz?.questions ? (
            <div className="py-4 space-y-6">
              {activeQuiz.quiz.questions.map((q, qi) => (
                <div key={q.id}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-orange-500 font-bold text-sm">{qi + 1}.</span>
                    {q.difficulty && <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-bold ${q.difficulty === 'advanced' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>{q.difficulty}</span>}
                    {q.type === 'open_response' && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 font-bold">Open Response</span>}
                  </div>
                  <div className="text-sm text-white font-medium mb-3">{q.question}</div>

                  {q.type === 'open_response' ? (
                    <textarea
                      value={quizAnswers[q.id] || ''}
                      onChange={(e) => setQuizAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                      placeholder="Write your response here. Explain your reasoning fully — like you're breaking down game film."
                      className="w-full bg-[#111] border border-gray-800 rounded-lg p-3 text-sm text-gray-300 min-h-[100px] focus:border-orange-500 focus:outline-none resize-y"
                      data-testid={`open-response-${q.id}`}
                    />
                  ) : (
                    <div className="space-y-2">
                      {q.options?.map((opt, oi) => (
                        <button
                          key={oi}
                          onClick={() => setQuizAnswers(prev => ({ ...prev, [q.id]: oi }))}
                          className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${
                            quizAnswers[q.id] === oi
                              ? 'border-orange-500 bg-orange-500/10 text-white'
                              : 'border-gray-800 bg-[#111] text-gray-400 hover:border-gray-600'
                          }`}
                        >
                          <span className="font-bold mr-2 text-gray-600">{String.fromCharCode(65 + oi)}.</span>
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button
                onClick={submitQuiz}
                disabled={submittingQuiz || Object.keys(quizAnswers).length < activeQuiz.quiz.questions.filter(q => q.type !== 'open_response').length}
                className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-6"
              >
                {submittingQuiz ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Assessment'}
              </Button>
              <p className="text-[10px] text-gray-600 text-center">Open-response answers will receive AI coaching notes after submission.</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Biometric Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={cancelCamera}>
        <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-black flex items-center gap-2">
              <Camera className="w-5 h-5 text-orange-500" /> Biometric Check-In
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-0 border-2 border-orange-500/30 rounded-lg pointer-events-none">
                <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-orange-500" />
                <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-orange-500" />
                <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-orange-500" />
                <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-orange-500" />
              </div>
              {checkingIn && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
                    <span className="text-sm text-gray-300">Verifying identity...</span>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 text-center">Position your face in the frame. <span className="text-orange-500 font-semibold">MediaPipe Holistic</span> verifies locally — no image leaves your device.</p>
            <div className="flex gap-3">
              <Button onClick={cancelCamera} variant="outline" className="flex-1 border-gray-700 text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={captureAndVerify}
                disabled={checkingIn}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold"
                data-testid="capture-verify-btn"
              >
                {checkingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-2" /> Verify & Check In</>}
              </Button>
            </div>
            <button onClick={manualCheckIn} className="w-full text-xs text-gray-600 hover:text-gray-400 text-center">
              Skip camera — use manual check-in
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drill Upload Dialog */}
      <Dialog open={showDrillUpload} onOpenChange={() => { setShowDrillUpload(false); setDrillResult(null); }}>
        <DialogContent className="bg-[#0a0a0a] border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-black flex items-center gap-2">
              <Video className="w-5 h-5 text-orange-500" /> Performance Upload
            </DialogTitle>
          </DialogHeader>
          {drillResult?.success ? (
            <div className="py-4 text-center">
              <div className={`text-5xl font-black mb-2 ${
                drillResult.performance?.score >= 85 ? 'text-green-400' :
                drillResult.performance?.score >= 60 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {drillResult.performance?.score}%
              </div>
              <div className={`text-sm font-bold uppercase mb-4 ${
                drillResult.performance?.recommendation === 'mastery' ? 'text-green-500' :
                drillResult.performance?.recommendation === 'corrective' ? 'text-red-500' : 'text-amber-500'
              }`}>
                {drillResult.performance?.recommendation === 'mastery' ? 'MASTERY ACHIEVED' :
                 drillResult.performance?.recommendation === 'corrective' ? 'CORRECTIVE DRILL NEEDED' : 'KEEP PROGRESSING'}
              </div>
              {drillResult.feedback?.text && (
                <div className="bg-[#111] rounded-lg p-3 text-sm text-gray-300 text-left mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mic className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-orange-500 font-bold uppercase">Coach Feedback</span>
                  </div>
                  {drillResult.feedback.text}
                </div>
              )}
              {drillResult.ncaa_status && !drillResult.ncaa_status.athletic_unlocked && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400 mb-4">
                  Athletic features locked — improve core GPA to continue.
                </div>
              )}
              <Button onClick={() => { setShowDrillUpload(false); setDrillResult(null); loadDashboard(); }} className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
                Done
              </Button>
            </div>
          ) : drillResult?.lockout ? (
            <div className="py-4 text-center">
              <Lock className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <div className="text-lg font-black text-red-400 mb-2">Athletic Features Locked</div>
              <p className="text-sm text-gray-400 mb-4">{drillResult.message}</p>
              <Button onClick={() => setShowDrillUpload(false)} className="w-full bg-gray-700 hover:bg-gray-600 text-white font-bold">
                Close
              </Button>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              <p className="text-sm text-gray-400">
                Upload a drill video — <span className="text-orange-500 font-semibold">MediaPipe Holistic</span> analyzes biomechanics on your device.
                Your video stays local; only the score feeds into your Recruit-Ready profile.
              </p>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center relative">
                {drillUploading ? (
                  <div>
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-3" />
                    <div className="text-sm text-gray-300 mb-2">Scoring biomechanics on-device...</div>
                    <div className="w-full bg-gray-800 rounded h-2 overflow-hidden">
                      <div
                        className="h-full bg-orange-500 transition-all duration-200"
                        style={{ width: `${Math.round(drillProgress * 100)}%` }}
                        data-testid="drill-progress-bar"
                      />
                    </div>
                    <div className="text-[10px] text-gray-600 mt-2 uppercase tracking-widest">
                      {Math.round(drillProgress * 100)}% — Frames analyzed
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <div className="text-gray-400 text-sm">Click to upload drill video</div>
                    <div className="text-gray-600 text-xs mt-1">MP4, MOV — up to ~15s analyzed</div>
                    <input type="file" accept="video/*" onChange={handleDrillUpload} className="hidden" data-testid="drill-file-input" />
                  </label>
                )}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-600 uppercase tracking-widest justify-center">
                <Shield className="w-3 h-3" />
                <span>On-Device · Zero API Cost · No Video Uploaded</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audio Briefing Indicator */}
      {playingBriefing && (
        <div className="fixed bottom-4 right-4 bg-orange-500 text-black px-4 py-2 rounded-full flex items-center gap-2 shadow-lg z-50 animate-pulse">
          <Mic className="w-4 h-4" />
          <span className="text-sm font-bold">Counselor speaking...</span>
        </div>
      )}
    </div>
  );
};

export default SchoolDashboard;
